import type { PropsWithChildren } from 'react'
import React, { useMemo } from 'react'

import { intl } from 'lang/intl'
import { usePanel } from '../../../firebase/hooks/usePanel'
import { useHumanParticipants } from '../../../webrtc/webRTCRoom'
import SpeakerGlyph from './SpeakerGlyph'
import useFeatures from '../../../sharedLegacy/hooks/useFeatures'

import './PanelMonitorContent.scss'

//
// PanelMonitorContent
//
// The panel monitor content component is representing the content part of the
// panel monitor. For now this should just reflect the number of speakers that
// will be present, should the host change to panel mode.
//

type PanelMonitorContentProps = PropsWithChildren<{
  tint: string
  isSelected?: boolean
  onClick: () => void
}>

const PanelMonitorContent = ({
  tint,
  isSelected,
  onClick,
}: PanelMonitorContentProps) => {
  const { screen, participantIds, mode } = usePanel()
  const participants = useHumanParticipants()
  const { HasPanelMixedModeEnabled } = useFeatures()

  const visibleParticipants = useMemo(() => {
    if (mode === 'auto') {
      return participants.map((p) => p.id)
    }

    const participantIdsInRoom = participants.map((p) => p.id)
    return participantIds.filter((i) => participantIdsInRoom.includes(i))
  }, [mode, participantIds, participants])

  const participantCount = visibleParticipants.length

  return (
    <button
      type="button"
      className="panel-monitor-content"
      onClick={onClick}
      aria-label={
        isSelected
          ? intl.formatMessage({
              defaultMessage: 'The selected speakers panel is shown',
            })
          : intl.formatMessage({
              defaultMessage:
                'Press enter or space to show the selected speakers panel',
            })
      }
    >
      <div
        className="speaker-glyphs-enclosure"
        data-number-of-speakers={participantCount}
      >
        {visibleParticipants.map((_, i) => (
          <div
            // eslint-disable-next-line react/no-array-index-key
            key={i}
            className="grid-item"
            style={{
              '--grid-area': `${String.fromCharCode('a'.charCodeAt(0) + i)}`,
            }}
          >
            <SpeakerGlyph tint={tint} />
          </div>
        ))}
      </div>
      {HasPanelMixedModeEnabled && screen && (
        <div className="panel-screen-item" />
      )}
    </button>
  )
}

export { PanelMonitorContent }
